import React, {useMemo} from 'react';

import {Dialog, DialogBody, DialogFooter, Button, VF, Row, Col, Card, ModalComponentProps, makeValidate, Yup} from '@mc/ui-v4';

import {Form, FormRenderProps} from 'react-final-form';

type FormProps = {
    offset: number;
    targetLayerId?: string | number;
};

type LayerOption = {
    id: string | number;
    nazev: string;
};

type Props = ModalComponentProps & {
    title: string;
    initialValues?: FormProps;
    targetLayerOptions?: LayerOption[];
    onSubmit: (data: FormProps) => void;
};

/**
 * Modální dialog "Vytvoření linie s odsazením".
 *
 * Odsazení se zadává v metrech a může být i záporné – znaménko určuje stranu, na kterou se
 * rovnoběžka vytvoří (kladná hodnota vpravo, záporná vlevo ve směru linie).
 *
 * @param title titulek dialogu
 * @param initialValues výchozí hodnoty formuláře
 * @param targetLayerOptions vrstvy pro vložení (pouze pokud je z čeho vybírat)
 * @param onClose zavření dialogu
 * @param onSubmit odeslání formuláře
 */

export const sizesSchema = Yup.object().shape({
    offset: Yup.number()
        .required('Zadejte odsazení v metrech')
        .notOneOf([0], 'Odsazení nesmí být nulové'),
});
export const LineOffsetModal: React.FC<Props> = ({title, initialValues, onClose, onSubmit, targetLayerOptions}) => {
    const validate = useMemo(() => makeValidate(sizesSchema), []);

    return (
        <Form<FormProps>
            initialValues={initialValues}
            validate={async data => {
                const errors = await validate(data);
                if (targetLayerOptions?.length > 0 && !data?.targetLayerId) {
                    return {...errors, targetLayerId: 'Vyberte cílovou vrstvu'};
                }
                return errors;
            }}
            onSubmit={onSubmit}
        >
            {({submitting, handleSubmit}: FormRenderProps<FormProps>) => {
                return (
                    <Dialog isOpen onClose={onClose} title={title}>
                        <DialogBody>
                            <Card mode={'edit'} className="p-2">
                                <Row>
                                    <Col>
                                        <VF.Number key={'offset'} disabled={submitting} name={'offset'} label={'Offset (m)'} required={true} decimal />
                                        <div className="text-muted">Kladná hodnota odsadí linii vpravo, záporná vlevo (ve směru linie).</div>
                                    </Col>
                                </Row>
                                {targetLayerOptions?.length > 0 && (
                                    <Row>
                                        <Col>
                                            <VF.SelectBox
                                                key={'targetLayerId'}
                                                name={'targetLayerId'}
                                                label={'Vrstva pro vložení'}
                                                items={targetLayerOptions}
                                                useIdAsValue
                                                required
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </Card>
                        </DialogBody>
                        <DialogFooter>
                            <Button danger disabled={submitting} onClick={onClose}>
                                Zrušit
                            </Button>
                            <Button style="primary" disabled={submitting} onClick={handleSubmit} type={'submit'}>
                                Vytvořit
                            </Button>
                        </DialogFooter>
                    </Dialog>
                );
            }}
        </Form>
    );
};
